import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../config/api';
import { useAdminViewerIp } from '../hooks/useAdminViewerIp';
import { buildExclusionSet, isExcludedFromAnalytics } from '../internalStaffIps';
import PageLayout from '../components/PageLayout';
import './EmailSubscriptionsPage.css';

interface EmailSubscription {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  confirmed: boolean;
  confirmed_at?: number;
  created_at: number;
  unsubscribed: boolean;
  unsubscribed_at?: number;
  ip_address?: string;
  browser_type?: string;
  os?: string;
  screen_resolution?: string;
}

interface SubscriptionsResponse {
  subscriptions: EmailSubscription[];
  count: number;
  last_key?: string;
}

type SubscriptionFilter = 'all' | 'confirmed' | 'pending' | 'unsubscribed';

type SortColumn =
  | 'email'
  | 'first_name'
  | 'last_name'
  | 'status'
  | 'created_at'
  | 'confirmed_at'
  | 'unsubscribed_at';

type SortDirection = 'asc' | 'desc';

function appendListFilterParams(params: URLSearchParams, filter: SubscriptionFilter): void {
  if (filter === 'confirmed') {
    params.append('confirmed', 'true');
    params.append('unsubscribed', 'false');
  } else if (filter === 'pending') {
    params.append('confirmed', 'false');
    params.append('unsubscribed', 'false');
  } else if (filter === 'unsubscribed') {
    params.append('unsubscribed', 'true');
  }
}

function subscriptionStatusLabel(sub: EmailSubscription): string {
  if (sub.unsubscribed) return 'Unsubscribed';
  if (sub.confirmed) return 'Confirmed';
  return 'Pending';
}

/** Stable ordering: Pending (0) < Confirmed (1) < Unsubscribed (2) */
function subscriptionStatusRank(sub: EmailSubscription): number {
  if (sub.unsubscribed) return 2;
  if (sub.confirmed) return 1;
  return 0;
}

function sortSubscriptions(
  rows: EmailSubscription[],
  sortColumn: SortColumn,
  sortDirection: SortDirection
): EmailSubscription[] {
  const dir = sortDirection === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    let cmp = 0;
    switch (sortColumn) {
      case 'email':
        cmp = (a.email || '').localeCompare(b.email || '', undefined, { sensitivity: 'base' });
        break;
      case 'first_name':
        cmp = (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' });
        break;
      case 'last_name':
        cmp = (a.last_name || '').localeCompare(b.last_name || '', undefined, { sensitivity: 'base' });
        break;
      case 'status':
        cmp = subscriptionStatusRank(a) - subscriptionStatusRank(b);
        break;
      case 'created_at':
        cmp = (a.created_at || 0) - (b.created_at || 0);
        break;
      case 'confirmed_at':
        cmp = (a.confirmed_at ?? 0) - (b.confirmed_at ?? 0);
        break;
      case 'unsubscribed_at':
        cmp = (a.unsubscribed_at ?? 0) - (b.unsubscribed_at ?? 0);
        break;
      default:
        cmp = 0;
    }
    if (cmp !== 0) return cmp * dir;
    return (a.id || '').localeCompare(b.id || '');
  });
}

function escapeCsvField(value: string): string {
  const normalized = value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (/[",\n]/.test(normalized)) {
    return `"${normalized.replace(/"/g, '""')}"`;
  }
  return normalized;
}

function buildSubscriptionsCsv(rows: EmailSubscription[]): string {
  const header = ['email', 'lastname', 'firstname', 'subscription_date', 'status'];
  const lines = [header.join(',')];
  for (const sub of rows) {
    const iso = new Date(sub.created_at * 1000).toISOString();
    lines.push(
      [
        escapeCsvField(sub.email || ''),
        escapeCsvField(sub.last_name || ''),
        escapeCsvField(sub.first_name || ''),
        escapeCsvField(iso),
        escapeCsvField(subscriptionStatusLabel(sub)),
      ].join(',')
    );
  }
  return lines.join('\r\n');
}

function triggerCsvDownload(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Local date/time, safe for filenames on Windows/macOS/Linux (no `:` or spaces). */
function formatLocalDateTimeForFilename(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}-${pad(
    d.getMinutes()
  )}-${pad(d.getSeconds())}`;
}

const EmailSubscriptionsPage: React.FC = () => {
  const { token } = useAuth();
  const viewerIp = useAdminViewerIp(token);
  const [subscriptions, setSubscriptions] = useState<EmailSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<SubscriptionFilter>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [excludeStaffFromDisplay, setExcludeStaffFromDisplay] = useState<boolean>(true);
  const [sortColumn, setSortColumn] = useState<SortColumn>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const fetchAllSubscriptionsForFilter = useCallback(
    async (filterValue: SubscriptionFilter): Promise<EmailSubscription[]> => {
      if (!token) return [];
      const accumulated: EmailSubscription[] = [];
      let lastKey: string | undefined;
      do {
        const params = new URLSearchParams();
        appendListFilterParams(params, filterValue);
        params.append('limit', '100');
        if (lastKey) params.append('last_key', lastKey);

        const response = await apiRequest<SubscriptionsResponse>(
          `/subscriptions?${params.toString()}`,
          token
        );
        accumulated.push(...(response.subscriptions || []));
        lastKey = response.last_key?.trim() || undefined;
      } while (lastKey);
      return accumulated;
    },
    [token]
  );

  const loadSubscriptions = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const all = await fetchAllSubscriptionsForFilter(filter);
      setSubscriptions(all);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load subscriptions');
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadSubscriptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filter]);

  const handleDelete = async (id: string, email: string) => {
    if (!token || !window.confirm(`Are you sure you want to delete the subscription for ${email}?`)) {
      return;
    }
    try {
      await apiRequest(`/subscriptions/${id}`, token, { method: 'DELETE' });
      await loadSubscriptions();
    } catch (err: any) {
      setError(err.message || 'Failed to delete subscription');
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleDownloadCsv = async () => {
    if (!token) return;
    try {
      setExporting(true);
      setError('');
      const rows = sortSubscriptions(
        await fetchAllSubscriptionsForFilter('all'),
        sortColumn,
        sortDirection
      );
      const csv = buildSubscriptionsCsv(rows);
      const stamp = formatLocalDateTimeForFilename(new Date());
      triggerCsvDownload(csv, `email-subscriptions-${stamp}.csv`);
    } catch (err: any) {
      setError(err.message || 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const formatTimestamp = (timestamp?: number): string => {
    if (!timestamp) return '-';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredSubscriptions = useMemo(() => {
    let filtered = subscriptions;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((sub) => {
        const emailMatch = sub.email?.toLowerCase().includes(term);
        const idMatch = sub.id?.toLowerCase().includes(term);
        const fn = sub.first_name?.toLowerCase().includes(term);
        const ln = sub.last_name?.toLowerCase().includes(term);
        return Boolean(emailMatch || idMatch || fn || ln);
      });
    }

    if (excludeStaffFromDisplay) {
      const set = buildExclusionSet(viewerIp);
      filtered = filtered.filter((sub) => !isExcludedFromAnalytics(sub.ip_address, set));
    }

    return filtered;
  }, [subscriptions, searchTerm, excludeStaffFromDisplay, viewerIp]);

  const sortedSubscriptions = useMemo(
    () => sortSubscriptions(filteredSubscriptions, sortColumn, sortDirection),
    [filteredSubscriptions, sortColumn, sortDirection]
  );

  const sortIndicator = (column: SortColumn) =>
    sortColumn === column ? (
      <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>
    ) : null;

  if (loading && subscriptions.length === 0) {
    return (
      <PageLayout title="Email Subscriptions">
        <div>Loading...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Email Subscriptions">
      {error && <div className="error-banner">{error}</div>}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          gap: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: '1rem', flex: 1, maxWidth: '720px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search by email, name, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              flex: '1 1 200px',
              fontSize: '0.9rem',
            }}
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as SubscriptionFilter)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem',
              cursor: 'pointer',
            }}
          >
            <option value="all">All</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="unsubscribed">Unsubscribed</option>
          </select>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={excludeStaffFromDisplay}
              onChange={(e) => setExcludeStaffFromDisplay(e.target.checked)}
            />
            Exclude Justin and Jeff
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn-sm btn-secondary-export"
            onClick={() => void handleDownloadCsv()}
            disabled={!token || exporting || loading}
          >
            {exporting ? 'Preparing export…' : 'Download All Records'}
          </button>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>
            Showing {sortedSubscriptions.length} of {subscriptions.length} subscriptions
          </div>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort('email')}>
                Email{sortIndicator('email')}
              </th>
              <th className="sortable" onClick={() => handleSort('first_name')}>
                First name{sortIndicator('first_name')}
              </th>
              <th className="sortable" onClick={() => handleSort('last_name')}>
                Last name{sortIndicator('last_name')}
              </th>
              <th className="sortable" onClick={() => handleSort('status')}>
                Status{sortIndicator('status')}
              </th>
              <th className="sortable" onClick={() => handleSort('created_at')}>
                Created{sortIndicator('created_at')}
              </th>
              <th className="sortable" onClick={() => handleSort('confirmed_at')}>
                Confirmed{sortIndicator('confirmed_at')}
              </th>
              <th className="sortable" onClick={() => handleSort('unsubscribed_at')}>
                Unsubscribed{sortIndicator('unsubscribed_at')}
              </th>
              <th>Metadata</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedSubscriptions.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  {loading
                    ? 'Loading...'
                    : subscriptions.length === 0
                      ? 'No subscriptions found.'
                      : searchTerm.trim()
                        ? 'No subscriptions match your search.'
                        : excludeStaffFromDisplay
                          ? 'No subscriptions left after excluding staff IPs (and your IP when known). Try turning off the filter or refining search.'
                          : 'No subscriptions found.'}
                </td>
              </tr>
            ) : (
              sortedSubscriptions.map((sub) => (
                <tr key={sub.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{sub.email}</td>
                  <td style={{ fontSize: '0.9rem' }}>{sub.first_name || '-'}</td>
                  <td style={{ fontSize: '0.9rem' }}>{sub.last_name || '-'}</td>
                  <td>
                    <span
                      className={`status-badge ${sub.confirmed ? 'confirmed' : 'pending'} ${
                        sub.unsubscribed ? 'unsubscribed' : ''
                      }`}
                    >
                      {subscriptionStatusLabel(sub)}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.85rem', color: '#666' }}>{formatTimestamp(sub.created_at)}</td>
                  <td style={{ fontSize: '0.85rem', color: '#666' }}>
                    {sub.confirmed ? formatTimestamp(sub.confirmed_at) : '-'}
                  </td>
                  <td style={{ fontSize: '0.85rem', color: '#666' }}>
                    {sub.unsubscribed ? formatTimestamp(sub.unsubscribed_at) : '-'}
                  </td>
                  <td style={{ fontSize: '0.8rem', color: '#999' }}>
                    {sub.os && sub.browser_type && (
                      <div>
                        {sub.os} / {sub.browser_type}
                        {sub.screen_resolution && ` (${sub.screen_resolution})`}
                      </div>
                    )}
                    {sub.ip_address && (
                      <div style={{ marginTop: '2px' }}>IP: {sub.ip_address}</div>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => handleDelete(sub.id, sub.email)}
                      className="btn-sm btn-danger"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
};

export default EmailSubscriptionsPage;
